/**
 * Overlays Page (per channel)
 *
 * Lists a channel's overlays and the system defaults it can adopt. From here a
 * streamer adopts a default (getting a one-time OBS URL + token), regenerates a
 * token, or opens an overlay in the editor.
 *
 * Route: /channels/:channelId/overlays
 *
 * Styling: synthwave design system (architecture/design-system.md) — semantic
 * tokens via .card/.btn/.input and synth-* / state-* / text-* / surface-* roles.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiErrorMessage } from '@/api/client'
import {
  listOverlays,
  adoptOverlay,
  regenerateToken,
  createCustomOverlay,
  type Overlay,
  type OverlayWithToken,
} from '@/api/overlaysApi'
import { useToast } from '@/components/Toast'
import {
  Layers,
  Plus,
  KeyRound,
  Copy,
  Check,
  AlertCircle,
  Loader2,
  ChevronRight,
  Pencil,
  Code2,
} from 'lucide-react'

/** A read-only field with a copy button — used for the one-time token + URL. */
function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard may be unavailable (insecure context); the value is still
      // selectable in the input as a fallback.
    }
  }

  return (
    <div>
      <label className="block text-sm text-text-secondary mb-2">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          readOnly
          value={value}
          onFocus={(e) => e.target.select()}
          className="input font-mono text-sm flex-1"
        />
        <button
          type="button"
          onClick={copy}
          className="btn-secondary flex items-center gap-2 shrink-0"
          title="Copy to clipboard"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

/** Modal shown once after adopt / regenerate, surfacing the token + OBS URL. */
function TokenModal({
  result,
  onClose,
}: {
  result: OverlayWithToken
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface-overlay rounded-xl border border-border p-6 w-full max-w-lg">
        <h2 className="text-xl font-semibold text-text-primary mb-2">
          Overlay URL ready
        </h2>
        <div className="mb-4 p-3 bg-state-warning-bg border border-state-warning rounded-lg flex items-start gap-2 text-state-warning text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>
            This is the only time the token is shown. Copy the URL into your OBS
            browser source now. If you lose it, regenerate a new one.
          </span>
        </div>

        <div className="space-y-4">
          <CopyField label="OBS browser source URL" value={result.overlay_url} />
          <CopyField label="Token" value={result.token} />
        </div>

        <div className="flex justify-end mt-6">
          <button type="button" onClick={onClose} className="btn-primary">
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

/** A row for one of the channel's own overlays. */
function OverlayRow({
  overlay,
  onEdit,
  onRegenerate,
  isBusy,
}: {
  overlay: Overlay
  onEdit: () => void
  onRegenerate: () => void
  isBusy: boolean
}) {
  return (
    <div className="card flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-synth-magenta/20 flex items-center justify-center shrink-0">
          <Layers className="text-synth-magenta" size={20} />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-text-primary truncate">{overlay.name}</h3>
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <span className="px-2 py-0.5 rounded-full bg-surface-overlay text-text-secondary">
              {overlay.overlay_type}
            </span>
            {overlay.source === 'custom_html' && (
              <span className="px-2 py-0.5 rounded-full bg-synth-magenta/20 text-synth-magenta">
                custom
              </span>
            )}
            <span>slot: {overlay.slot}</span>
            {!overlay.enabled && (
              <span className="text-text-muted">· disabled</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onRegenerate}
          className="p-2 text-text-secondary hover:text-synth-magenta hover:bg-surface-overlay rounded-lg transition-colors"
          title="Regenerate token (invalidates the old URL)"
          disabled={isBusy}
        >
          {isBusy ? (
            <Loader2 className="animate-spin" size={18} />
          ) : (
            <KeyRound size={18} />
          )}
        </button>
        <button
          onClick={onEdit}
          className="btn-secondary flex items-center gap-2"
          title="Edit overlay"
        >
          <Pencil size={16} />
          Edit
          <ChevronRight size={16} className="text-text-muted" />
        </button>
      </div>
    </div>
  )
}

/** A row for an adoptable system default. */
function DefaultRow({
  overlay,
  onAdopt,
  isBusy,
}: {
  overlay: Overlay
  onAdopt: () => void
  isBusy: boolean
}) {
  return (
    <div className="card flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-surface-overlay flex items-center justify-center shrink-0">
          <Layers className="text-text-secondary" size={20} />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-text-primary truncate">{overlay.name}</h3>
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <span className="px-2 py-0.5 rounded-full bg-surface-overlay text-text-secondary">
              {overlay.overlay_type}
            </span>
            <span>slot: {overlay.slot}</span>
            <span className="text-text-muted">· system default</span>
          </div>
        </div>
      </div>
      <button
        onClick={onAdopt}
        className="btn-primary flex items-center gap-2 shrink-0"
        disabled={isBusy}
      >
        {isBusy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
        Use this
      </button>
    </div>
  )
}

/** Modal to create a new custom_html overlay (name + slot, then edit). */
function CreateCustomModal({
  onCancel,
  onCreate,
  creating,
}: {
  onCancel: () => void
  onCreate: (name: string, slot: string) => void
  creating: boolean
}) {
  const [name, setName] = useState('My custom overlay')
  const [slot, setSlot] = useState('main_alert')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-surface-overlay rounded-xl border border-border p-6 w-full max-w-md space-y-4">
        <h2 className="text-xl font-semibold text-text-primary">New custom overlay</h2>
        <div>
          <label className="block text-sm text-text-secondary mb-2">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm text-text-secondary mb-2">Slot</label>
          <input className="input" value={slot} onChange={(e) => setSlot(e.target.value)} />
          <p className="text-xs text-text-muted mt-1">
            The logical slot it fills (e.g. <code className="text-text-secondary">main_alert</code>). For a
            given slot, the overlay edited most recently is the one that renders.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => onCreate(name.trim() || 'Custom overlay', slot.trim() || 'main_alert')}
            className="btn-primary flex items-center gap-2"
            disabled={creating}
          >
            {creating ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
            Create &amp; edit
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Overlays() {
  const navigate = useNavigate()
  const { channelId } = useParams<{ channelId: string }>()
  const chId = Number(channelId)

  const [overlays, setOverlays] = useState<Overlay[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const { error } = useToast()
  const [tokenResult, setTokenResult] = useState<OverlayWithToken | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    try {
      const data = await listOverlays(chId)
      setOverlays(data)
      setLoadError(null)
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Failed to load overlays'))
    } finally {
      setIsLoading(false)
    }
  }, [chId])

  useEffect(() => {
    if (!Number.isFinite(chId)) {
      setLoadError('Invalid channel')
      setIsLoading(false)
      return
    }
    load()
  }, [chId, load])

  // The channel's own overlays vs. adoptable system defaults.
  const owned = overlays.filter((o) => o.channel_id === chId)
  const defaults = overlays.filter((o) => o.channel_id === null)
  // Only offer defaults for slots the channel hasn't already filled.
  const ownedSlots = new Set(owned.map((o) => o.slot))
  const adoptable = defaults.filter((o) => !ownedSlots.has(o.slot))

  const handleAdopt = async (slot: string) => {
    setBusyId(`default:${slot}`)
    try {
      const result = await adoptOverlay(chId, slot)
      setTokenResult(result)
      await load()
      // Success needs no toast: the token panel that opens IS the outcome,
      // and it carries the one-time plaintext that must not be buried under
      // a notification that dismisses itself.
    } catch (err) {
      error(apiErrorMessage(err, 'Failed to adopt overlay'))
    } finally {
      setBusyId(null)
    }
  }

  const handleRegenerate = async (overlayId: string) => {
    setBusyId(overlayId)
    try {
      const result = await regenerateToken(overlayId)
      setTokenResult(result)
    } catch (err) {
      error(apiErrorMessage(err, 'Failed to regenerate token'))
    } finally {
      setBusyId(null)
    }
  }

  const handleCreateCustom = async (name: string, slot: string) => {
    setCreating(true)
    try {
      const ov = await createCustomOverlay({ channel_id: chId, slot, name })
      navigate(`/channels/${chId}/overlays/custom/${ov.id}`)
    } catch (err) {
      // Success is the navigate() above -- the editor opening is the report.
      error(apiErrorMessage(err, 'Failed to create overlay'))
      setCreating(false)
      setShowCreate(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-synth-magenta" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            onClick={() => navigate(`/channels/${chId}`)}
            className="text-sm text-text-secondary hover:text-text-primary mb-2"
          >
            ← Back to channel
          </button>
          <h2 className="text-2xl font-bold text-text-primary">Overlays</h2>
          <p className="text-text-secondary">
            Alerts and widgets for your OBS browser sources. Adopt a default to get
            a URL, then customise it in the editor.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-primary flex items-center gap-2 shrink-0 mt-8"
        >
          <Code2 size={16} />
          New custom overlay
        </button>
      </div>

      {/* Load failure only. Mutation outcomes go to the toast queue -- a
          banner for those left the last failure pinned to the page long after
          the click that caused it. */}
      {loadError && (
        <div className="bg-state-danger-bg border border-state-danger rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-state-danger" size={20} />
          <span className="text-state-danger">{loadError}</span>
        </div>
      )}

      {/* The channel's overlays */}
      <section className="space-y-3">
        <h3 className="text-sm font-medium uppercase tracking-wide text-text-muted">
          Your overlays
        </h3>
        {owned.length > 0 ? (
          owned.map((o) => (
            <OverlayRow
              key={o.id}
              overlay={o}
              onEdit={() =>
                navigate(
                  o.source === 'custom_html'
                    ? `/channels/${chId}/overlays/custom/${o.id}`
                    : `/channels/${chId}/overlays/${o.id}`,
                )
              }
              onRegenerate={() => handleRegenerate(o.id)}
              isBusy={busyId === o.id}
            />
          ))
        ) : (
          <div className="card text-center py-8 text-text-secondary">
            No overlays yet. Adopt a default below to get started.
          </div>
        )}
      </section>

      {/* Adoptable defaults */}
      {adoptable.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-medium uppercase tracking-wide text-text-muted">
            Available defaults
          </h3>
          {adoptable.map((o) => (
            <DefaultRow
              key={o.id}
              overlay={o}
              onAdopt={() => handleAdopt(o.slot)}
              isBusy={busyId === `default:${o.slot}`}
            />
          ))}
        </section>
      )}

      {tokenResult && (
        <TokenModal result={tokenResult} onClose={() => setTokenResult(null)} />
      )}

      {showCreate && (
        <CreateCustomModal
          onCancel={() => setShowCreate(false)}
          onCreate={handleCreateCustom}
          creating={creating}
        />
      )}
    </div>
  )
}
